/**
 * Member-360 (Screen C) — the composite single-record view. Consumes GET /participants/:key/360 and
 * renders a STICKY identity header + a TABBED section nav over the self-provenanced Section<T> payload.
 * Each tab renders one isolated, badged section: clinical-REAL sections (problem-list, vitals,
 * encounters, care-plan, goals) sit beside DEFERRED sections (care-gaps, medications) and DISABLED
 * tabs whose data is not loaded here (quality, HCC — they live on their own surfaces / await a feed).
 * Every section's badge derives from its OWN section.provenance via ProvenanceBadge (never hand-tagged).
 * Per-section isolation: an "error" section shows the sanitized section.error only (never a stack/SQL/
 * PHI); an "empty" section shows the honest DEFERRED/real-empty state (never a silent zero). The Problem
 * List tab adds client-side filter/search/sort over the ALREADY-loaded real rows (a small per-member set
 * — sanctioned client-side filtering; the server endpoint independently accepts the same filters).
 * Minimum-necessary fields only (no MRN/medicare_id/medicaid_id). Section rows are never console.logged.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useParams, useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import {
  SkyBadge,
  SkyCard,
  SkyDataGrid,
  SkyDropdown,
  SkyInput,
  SkyLink,
  SkyTabList,
  SkyText,
  SkyLoader,
  SkyMessageBar,
  type SkyBadgeColor,
  type SkyDataGridColumn,
  type TabItem,
} from "@skypointcloud/skypoint-ui";
import type {
  Member360,
  Section,
  MemberHeader,
  Condition,
  Vital,
  Encounter,
  CarePlanBundle,
  CareGap,
  Medication,
  Allergy,
  Immunization,
  Procedure,
  Coverage,
  DiagnosticReport,
} from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { member360Schema } from "../lib/schemas/member";
import { useExemplarMember } from "../lib/useExemplarMember";
import { ageLabel } from "../lib/age";
import { humanizeToken } from "../lib/copy";
import { deriveBadge } from "../provenance/deriveBadge";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

/** Render-shape guard: coerce a section payload to a typed array only when it genuinely IS an array, so a
 *  drifted non-array payload (behind the ErrorBoundary) degrades to an empty grid rather than throwing in
 *  the grid's row map. */
function asRows<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

/** SAFETY-CRITICAL drift guard (allergies, medications). asRows() collapses ANY non-array to [], so a
 *  DRIFTED non-array-but-truthy payload on an 'ok' section would render an AFFIRMATIVE empty ("No known
 *  allergies recorded" / "No medications on record") - a false-negative safety signal. This distinguishes
 *  a legitimate empty (null/undefined -> render the honest empty grid, returns null here) from contract
 *  drift (a truthy non-array shape -> a holding state, NEVER an affirmative "none"). The row of REAL data
 *  we cannot parse is surfaced as UNAVAILABLE, not as absence. */
function safetyDriftNode(value: unknown, subject: string): ReactNode | null {
  if (Array.isArray(value) || value == null) return null;
  return (
    <SkyMessageBar
      intent="warning"
      title={`${subject} could not be displayed`}
      message={`The ${subject.toLowerCase()} data did not load in the expected format, so it is shown as unavailable, not as none. Reload the record or open the dedicated screen; do not read this as "none recorded" before prescribing or ordering.`}
    />
  );
}

/** The tab set. ENABLED tabs map to a section the /360 composite returns (goals is derived from the
 *  care-plan bundle). DEFERRED tabs are DISABLED here: their data is not part of this composite — they
 *  surface on their own screens (Quality) or await a feed (HCC, capitation). Never hidden, never faked. */
type TabDef = { value: string; label: string; section?: string; deferred?: boolean; deferredFeed?: string };
const TAB_DEFS: TabDef[] = [
  { value: "allergies", label: "Allergies", section: "allergies" },
  { value: "problem-list", label: "Problem List", section: "problem-list" },
  { value: "vitals", label: "Vitals", section: "vitals" },
  { value: "encounters", label: "Encounters", section: "encounters" },
  { value: "immunizations", label: "Immunizations", section: "immunizations" },
  { value: "procedures", label: "Procedures", section: "procedures" },
  { value: "diagnostic-reports", label: "Diagnostic Reports", section: "diagnostic-reports" },
  { value: "coverage", label: "Coverage", section: "coverage" },
  { value: "care-plan", label: "Care Plan", section: "care-plan" },
  { value: "goals", label: "Goals", section: "care-plan" },
  { value: "care-gaps", label: "Care Gaps", section: "care-gaps" },
  { value: "medications", label: "Medications", section: "medications" },
  { value: "quality", label: "Quality", deferred: true, deferredFeed: "Quality surface" },
  { value: "hcc", label: "HCC", deferred: true, deferredFeed: "capitation feed" },
];

/** P2 tab-overflow fix — the ~12 primary tabs are grouped into clinically-meaningful CLUSTERS that WRAP
 *  (Clinical / Coverage / Plan) instead of one horizontal-scroll row that clipped the last tab. The
 *  high-use tabs (Allergies, Problem List, Vitals, Encounters, Medications) lead the Clinical cluster so
 *  they are NEVER clipped, and every tab stays reachable on any viewport (the row wraps, it never scrolls
 *  a tab out of reach). Allergies still leads (safety). Deep-linking (?tab=) is unaffected. Any primary
 *  tab not listed here would still render via the trailing fallback cluster, so a new tab is never lost. */
const TAB_CLUSTERS: ReadonlyArray<{ label: string; values: ReadonlyArray<string> }> = [
  {
    label: "Clinical",
    values: [
      "allergies",
      "problem-list",
      "vitals",
      "encounters",
      "medications",
      "immunizations",
      "procedures",
      "diagnostic-reports",
    ],
  },
  { label: "Coverage", values: ["coverage", "care-gaps"] },
  { label: "Plan", values: ["care-plan", "goals"] },
];

/** FIX 2 — honest cap labeling. The /360 composite is a SUMMARY: some sections are limited (encounters
 *  to the most-recent 50; vitals to latest-per-code) and the payload does not carry the member's true
 *  total. For sections that have a dedicated member-keyed full screen we surface a "view full history"
 *  affordance that deep-links to that screen preselected to THIS member (?member=). `capped` sections
 *  add an honest "Showing the most recent N" sub-label (never presented as the member's total). */
const FULL_SCREEN: Record<string, { screen: string; to: (k: string) => string; capped?: boolean }> = {
  vitals: { screen: "Vitals", to: (k) => `/vitals?member=${k}` },
  encounters: { screen: "Encounters", to: (k) => `/encounters?member=${k}`, capped: true },
  "care-plan": { screen: "Care Plans & IDT", to: (k) => `/idt?member=${k}` },
  goals: { screen: "Care Plans & IDT", to: (k) => `/idt?member=${k}` },
  medications: { screen: "Medications", to: (k) => `/medications?member=${k}` },
};

const conditionColumns: SkyDataGridColumn<Condition>[] = [
  { key: "diagnosis_code", header: "Code (ICD-10)", width: "14%" },
  { key: "diagnosis_display", header: "Condition", width: "34%" },
  {
    key: "clinical_status",
    header: "Clinical status",
    width: "16%",
    // H5: status by color + LABEL (never a bare color). The chip carries the verbatim status text.
    renderCell: (_v, row) =>
      row.clinical_status ? (
        <SkyBadge color={clinicalStatusColor(row.clinical_status)} appearance="tint" size="small">
          {row.clinical_status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "verification_status", header: "Verification", width: "13%" },
  { key: "code_system", header: "System", width: "11%" },
  { key: "recorded_date", header: "Recorded", width: "12%" },
];

const vitalColumns: SkyDataGridColumn<Vital>[] = [
  // The vital is named by its REAL LOINC display (resolved via gold.dim_code on the /360 vitals section);
  // the opaque code_key surrogate is only a last-resort fallback when no dim name resolved (never faked).
  {
    key: "code_key",
    header: "Vital",
    width: "20%",
    renderCell: (_v, row) => {
      const name = row.code_display?.trim();
      if (name) return name;
      return <span className="text-neutral-500">{`#${row.code_key}`}</span>;
    },
  },
  { key: "effective_date", header: "Date", width: "22%" },
  {
    key: "value_quantity",
    header: "Value",
    width: "30%",
    align: "right",
    renderCell: (_v, row) => formatVital(row),
  },
  {
    key: "is_coded_flag",
    header: "Coded",
    width: "28%",
    renderCell: (_v, row) => (row.is_coded_flag ? "Yes" : "No"),
  },
];

const encounterColumns: SkyDataGridColumn<Encounter>[] = [
  { key: "period_start", header: "Date", width: "18%" },
  { key: "encounter_class", header: "Class", width: "14%" },
  { key: "encounter_type_display", header: "Type", width: "28%" },
  { key: "place_of_service", header: "Place", width: "14%" },
  {
    key: "is_telehealth_flag",
    header: "Telehealth",
    width: "12%",
    renderCell: (_v, row) => (row.is_telehealth_flag ? "Yes" : "No"),
  },
  {
    key: "practitioner_last_name",
    header: "Practitioner",
    width: "14%",
    renderCell: (_v, row) => practitionerLabel(row),
  },
];

const carePlanColumns: SkyDataGridColumn<import("@shared/contracts").CarePlan>[] = [
  { key: "category_code", header: "Category", width: "26%" },
  {
    key: "status",
    header: "Status",
    width: "20%",
    // H5: status by color + LABEL (never a bare color).
    renderCell: (_v, row) =>
      row.status ? (
        <SkyBadge color={carePlanStatusColor(row.status)} appearance="tint" size="small">
          {row.status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "intent", header: "Intent", width: "18%" },
  { key: "period_start", header: "Start", width: "18%" },
  { key: "period_end", header: "End", width: "18%" },
];

const goalColumns: SkyDataGridColumn<import("@shared/contracts").Goal>[] = [
  { key: "description", header: "Goal (coded)", width: "36%" },
  {
    key: "lifecycle_status",
    header: "Lifecycle",
    width: "22%",
    renderCell: (_v, row) =>
      row.lifecycle_status ? (
        <SkyBadge color={goalLifecycleColor(row.lifecycle_status)} appearance="tint" size="small">
          {row.lifecycle_status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  {
    key: "achievement_status",
    header: "Achievement",
    width: "24%",
    renderCell: (_v, row) =>
      row.achievement_status ? (
        <SkyBadge color={goalAchievementColor(row.achievement_status)} appearance="tint" size="small">
          {row.achievement_status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "due_date", header: "Due", width: "18%" },
];

const careGapColumns: SkyDataGridColumn<CareGap>[] = [
  {
    key: "measure_name",
    header: "Measure",
    width: "34%",
    // H3 PACE suppression: PUBLIC measure NAME + DOMAIN only (resolved server-side via gold.dim_measure).
    // The raw HEDIS/STARS measure_code / PACE rate identity is NEVER bound or displayed here.
    renderCell: (_v, row) => (
      <div className="flex flex-col">
        <SkyText size={300} weight="medium">
          {row.measure_name ?? "Care indicator"}
        </SkyText>
        {row.domain && <SkyText size={200}>{humanizeToken(row.domain)}</SkyText>}
      </div>
    ),
  },
  { key: "status", header: "Status", width: "22%" },
  { key: "due_date", header: "Due", width: "22%" },
  { key: "days_remaining", header: "Days remaining", width: "22%", align: "right" },
];

const medicationColumns: SkyDataGridColumn<Medication>[] = [
  {
    key: "medication_display",
    header: "Medication",
    width: "34%",
    // L1 field-level honesty: the clinically-primary drug NAME is null on every row in the current silver
    // extract. Render an explicit DEFERRED lane (mirroring the Diagnostic Reports result-value treatment)
    // instead of a quiet "Not recorded", so the tab never reads as fully-REAL while the primary field is
    // unknown. The ORDER row stays REAL; only this field is DEFERRED. Never blank, never invented.
    renderCell: (_v, row) =>
      row.medication_display ?? (
        <SkyBadge color="subtle" appearance="tint" size="small">
          {row.drug_name_status === "deferred" ? "Awaiting feed (RxNorm)" : "Not recorded"}
        </SkyBadge>
      ),
  },
  {
    key: "status",
    header: "Status",
    width: "20%",
    renderCell: (_v, row) =>
      row.status ? (
        <SkyBadge color={medStatusColor(row.status)} appearance="tint" size="small">
          {row.status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "intent", header: "Intent", width: "22%" },
  { key: "authored_on", header: "Authored", width: "24%" },
];

const allergyColumns: SkyDataGridColumn<Allergy>[] = [
  {
    key: "substance_display",
    header: "Substance",
    width: "26%",
    renderCell: (_v, row) => row.substance_display ?? "Not recorded",
  },
  {
    key: "criticality",
    header: "Criticality",
    width: "18%",
    // H5: status by color + LABEL — criticality drives the danger/warning color.
    renderCell: (_v, row) =>
      row.criticality ? (
        <SkyBadge color={criticalityColor(row.criticality)} appearance="tint" size="small">
          {row.criticality}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  {
    key: "clinical_status",
    header: "Clinical status",
    width: "16%",
    renderCell: (_v, row) =>
      row.clinical_status ? (
        <SkyBadge color={row.clinical_status.toLowerCase() === "active" ? "informative" : "subtle"} appearance="tint" size="small">
          {row.clinical_status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "verification_status", header: "Verification", width: "14%" },
  { key: "category", header: "Category", width: "14%", renderCell: (_v, row) => row.category ?? "Not recorded" },
  { key: "reaction_severity", header: "Reaction", width: "12%", renderCell: (_v, row) => row.reaction_severity ?? "Not recorded" },
];

const immunizationColumns: SkyDataGridColumn<Immunization>[] = [
  {
    key: "vaccine_name",
    header: "Vaccine",
    width: "34%",
    // Vaccine name resolved via gold.dim_vaccine; honest "Not recorded" when the dim did not resolve.
    renderCell: (_v, row) => row.vaccine_name ?? "Not recorded",
  },
  { key: "cvx_code", header: "CVX", width: "12%", renderCell: (_v, row) => row.cvx_code ?? "Not recorded" },
  {
    key: "status",
    header: "Status",
    width: "20%",
    // H5: status by color + LABEL. A 'not-done' is a real miss/refusal (warning), never hidden.
    renderCell: (_v, row) =>
      row.status ? (
        <SkyBadge color={immunizationStatusColor(row.status)} appearance="tint" size="small">
          {row.status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "occurrence_date", header: "Date", width: "18%", renderCell: (_v, row) => row.occurrence_date ?? "Not recorded" },
  { key: "series_designation", header: "Series", width: "16%", renderCell: (_v, row) => row.series_designation ?? "Not recorded" },
];

const procedureColumns: SkyDataGridColumn<Procedure>[] = [
  {
    key: "procedure_display",
    header: "Procedure",
    width: "40%",
    // Coded procedure name resolved via gold.dim_code; honest "Not recorded" when uncoded.
    renderCell: (_v, row) => row.procedure_display ?? "Not recorded",
  },
  { key: "code", header: "Code (CPT/HCPCS)", width: "18%", renderCell: (_v, row) => row.code ?? "Not recorded" },
  { key: "code_system", header: "System", width: "12%", renderCell: (_v, row) => row.code_system ?? "Not recorded" },
  {
    key: "status",
    header: "Status",
    width: "16%",
    renderCell: (_v, row) =>
      row.status ? (
        <SkyBadge color={procedureStatusColor(row.status)} appearance="tint" size="small">
          {row.status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  // performed_date is NULL for most rows in this extract: honest "Not recorded", never faked.
  { key: "performed_date", header: "Performed", width: "14%", renderCell: (_v, row) => row.performed_date ?? "Not recorded" },
];

const diagnosticReportColumns: SkyDataGridColumn<DiagnosticReport>[] = [
  {
    key: "report_name",
    header: "Report / test",
    width: "30%",
    // Report name resolved via gold.dim_code; honest "Report name not coded" when the code did not resolve.
    renderCell: (_v, row) => row.report_name ?? "Report name not coded",
  },
  { key: "code", header: "Code", width: "12%", renderCell: (_v, row) => row.code ?? "Not recorded" },
  {
    key: "status",
    header: "Status",
    width: "12%",
    // H5: status by color + LABEL. final/partial shown verbatim (a partial is a real interim report).
    renderCell: (_v, row) =>
      row.status ? (
        <SkyBadge color={diagnosticReportStatusColor(row.status)} appearance="tint" size="small">
          {row.status}
        </SkyBadge>
      ) : (
        "Not recorded"
      ),
  },
  { key: "issued_date", header: "Issued", width: "13%", renderCell: (_v, row) => row.issued_date ?? "Not recorded" },
  {
    key: "encounter_key",
    header: "Encounter",
    width: "13%",
    // The linked encounter surrogate (opaque id) ties the report to its visit; honest "Not recorded" if null.
    renderCell: (_v, row) =>
      row.encounter_key ? <span className="text-neutral-500">{`#${row.encounter_key}`}</span> : "Not recorded",
  },
  {
    // A4 HONESTY: the numeric result VALUE is DEFERRED — the gold fact carries no value column (the numeric
    // LOINC result feed / HL7 ORU has not landed). Every row shows a fixed "Awaiting feed" marker so the tab
    // never implies a value it does not have and NEVER fabricates one.
    key: "result_value",
    header: "Result value",
    width: "20%",
    renderCell: () => (
      <SkyBadge color="subtle" appearance="tint" size="small">
        Awaiting feed (HL7 ORU)
      </SkyBadge>
    ),
  },
];

const coverageColumns: SkyDataGridColumn<Coverage>[] = [
  { key: "plan_name", header: "Plan", width: "30%", renderCell: (_v, row) => row.plan_name ?? "Not recorded" },
  { key: "plan_type_code", header: "Plan type", width: "16%", renderCell: (_v, row) => row.plan_type_code ?? "Not recorded" },
  // payor is NULL across the current eCW extract: honest "Not recorded", never faked.
  { key: "payor", header: "Payor", width: "18%", renderCell: (_v, row) => row.payor ?? "Not recorded" },
  { key: "effective_from", header: "From", width: "13%", renderCell: (_v, row) => row.effective_from ?? "Not recorded" },
  { key: "effective_to", header: "To", width: "11%", renderCell: (_v, row) => row.effective_to ?? "Not recorded" },
  {
    key: "current_flag",
    header: "Current",
    width: "12%",
    // H5: status by color + LABEL — current plans are highlighted, prior plans read "Prior".
    renderCell: (_v, row) => (
      <SkyBadge color={row.current_flag ? "success" : "subtle"} appearance="tint" size="small">
        {row.current_flag ? "Current" : "Prior"}
      </SkyBadge>
    ),
  },
];

/** FHIR Immunization.status -> semantic badge color (status by color + label; not-done is a real miss). */
function immunizationStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "completed") return "success";
  if (s === "not-done") return "warning";
  if (s === "entered-in-error") return "danger";
  return "subtle";
}

/** FHIR Procedure.status -> semantic badge color. */
function procedureStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "completed") return "success";
  if (s === "in-progress" || s === "preparation") return "informative";
  if (s === "not-done" || s === "stopped") return "warning";
  if (s === "entered-in-error") return "danger";
  return "subtle";
}

/** FHIR DiagnosticReport.status -> semantic badge color (status by color + LABEL; partial is interim). */
function diagnosticReportStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "final" || s === "amended" || s === "corrected") return "success";
  if (s === "partial" || s === "preliminary" || s === "registered") return "informative";
  if (s === "cancelled" || s === "entered-in-error") return "danger";
  return "subtle";
}

/** FHIR AllergyIntolerance.criticality -> semantic badge color (high = danger, never amber-brand). */
function criticalityColor(criticality: string): SkyBadgeColor {
  const c = criticality.toLowerCase();
  if (c === "high") return "danger";
  if (c === "low") return "warning";
  return "subtle"; // unable-to-assess / unknown
}

/** MedicationRequest.status -> semantic badge color (status by color + LABEL). */
function medStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active") return "informative";
  if (s === "completed") return "success";
  if (s === "on-hold") return "warning";
  if (s === "stopped" || s === "cancelled" || s === "entered-in-error") return "danger";
  return "subtle";
}

/** Map a FHIR Condition.clinicalStatus to a semantic badge color (status by color + label; never amber-brand). */
function clinicalStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active") return "informative";
  if (s === "resolved" || s === "remission") return "success";
  if (s === "recurrence" || s === "relapse") return "warning";
  return "subtle"; // inactive / unknown
}

/** FHIR CarePlan.status -> semantic badge color (consistent with the Care Plans & IDT screen). */
function carePlanStatusColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active") return "informative";
  if (s === "completed") return "success";
  if (s === "on-hold") return "warning";
  if (s === "revoked" || s === "entered-in-error") return "danger";
  return "subtle"; // draft / unknown
}

/** FHIR Goal.lifecycleStatus -> semantic badge color. */
function goalLifecycleColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "active" || s === "accepted") return "informative";
  if (s === "completed") return "success";
  if (s === "on-hold") return "warning";
  if (s === "cancelled" || s === "rejected" || s === "entered-in-error") return "danger";
  return "subtle"; // proposed / planned
}

/** FHIR Goal.achievementStatus -> semantic badge color. */
function goalAchievementColor(status: string): SkyBadgeColor {
  const s = status.toLowerCase();
  if (s === "achieved" || s === "sustaining") return "success";
  if (s === "improving" || s === "in-progress") return "informative";
  if (s === "worsening" || s === "no-progress" || s === "not-achieved" || s === "not-attainable")
    return "warning";
  return "subtle"; // no-change
}

function formatVital(row: Vital): string {
  if (row.component_systolic != null && row.component_diastolic != null) {
    return `${row.component_systolic}/${row.component_diastolic}${row.value_unit ? ` ${row.value_unit}` : ""}`;
  }
  if (row.value_quantity != null) {
    return `${row.value_quantity}${row.value_unit ? ` ${row.value_unit}` : ""}`;
  }
  return "Not recorded";
}

function practitionerLabel(row: Encounter): string {
  const last = row.practitioner_last_name ?? "";
  const first = row.practitioner_first_name ?? "";
  const label = [last, first].filter(Boolean).join(", ");
  return label || "Not recorded";
}

/** The honest non-ok state for any section: a sanitized error, a real-empty "None on record", or a
 *  feed-gated "Awaiting feed" — never a silent zero. Returns null when the section is ok (render data). */
function sectionStateNode(section: Section<unknown>): ReactNode | null {
  if (section.status === "error") {
    return (
      <SkyMessageBar
        intent="warning"
        title="Section unavailable"
        message={section.error ?? "section unavailable"}
      />
    );
  }
  if (section.status === "empty") {
    // M2: distinguish a genuine REAL-empty ("None on record" — a real, complete signal) from a
    // feed-gated DEFERRED empty ("Awaiting feed"). The server's provenance carries the distinction.
    const realEmpty = deriveBadge(section.provenance).tier === "REAL";
    return realEmpty ? (
      <SkyText size={300}>
        None on record. This section is complete and has no entries (a real empty, not a feed gap).
      </SkyText>
    ) : (
      <SkyText size={300}>Awaiting feed. This section has no backing data yet (not a zero).</SkyText>
    );
  }
  return null;
}

/** Distinct, defined values for a Condition column (drives the filter dropdown options — only offers
 *  values that actually exist in the loaded rows, so a filter can never claim data we don't have). */
function distinctValues(rows: Condition[], key: keyof Condition): string[] {
  return [...new Set(rows.map((r) => r[key]).filter((v): v is string => Boolean(v)))].sort();
}

/** Problem List body — client-side filter/search/sort over the already-loaded REAL conditions (a small
 *  per-member set; the cross-cutting rule sanctions client-side filtering of fully-loaded small sets).
 *  Every control is backed by a REAL op_fact_condition column; the standalone endpoint accepts the same. */
function ProblemListBody({ rows }: { rows: Condition[] }) {
  const [search, setSearch] = useState("");
  const [clinicalStatus, setClinicalStatus] = useState("");
  const [category, setCategory] = useState("");
  const [codeSystem, setCodeSystem] = useState("");
  const [verification, setVerification] = useState("");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const opts = useMemo(
    () => ({
      clinical_status: distinctValues(rows, "clinical_status"),
      category: distinctValues(rows, "category"),
      code_system: distinctValues(rows, "code_system"),
      verification_status: distinctValues(rows, "verification_status"),
    }),
    [rows],
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (clinicalStatus && r.clinical_status !== clinicalStatus) return false;
      if (category && r.category !== category) return false;
      if (codeSystem && r.code_system !== codeSystem) return false;
      if (verification && r.verification_status !== verification) return false;
      if (s) {
        const hay = `${r.diagnosis_display ?? ""} ${r.diagnosis_code ?? ""}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
    // Sort by recorded_date (ISO strings sort lexicographically); nulls sink to the bottom.
    return [...out].sort((a, b) => {
      const av = a.recorded_date ?? "";
      const bv = b.recorded_date ?? "";
      if (!av && !bv) return 0;
      if (!av) return 1;
      if (!bv) return -1;
      return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [rows, search, clinicalStatus, category, codeSystem, verification, sortDir]);

  const toOptions = (vals: string[]) => vals.map((v) => ({ value: v, text: v }));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64 max-w-full">
          <SkyInput
            label="Search problems"
            placeholder="Display text or code (e.g. E11.22)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            clearable
            onClear={() => setSearch("")}
            contentBefore={<Search size={16} aria-hidden />}
          />
        </div>
        <div className="w-44 max-w-full">
          <SkyDropdown
            label="Clinical status"
            options={toOptions(opts.clinical_status)}
            placeholder="All"
            clearable
            selectedOptions={clinicalStatus ? [clinicalStatus] : []}
            onOptionSelect={(v) => setClinicalStatus(v[0] ?? "")}
          />
        </div>
        <div className="w-44 max-w-full">
          <SkyDropdown
            label="Category"
            options={toOptions(opts.category)}
            placeholder="All"
            clearable
            selectedOptions={category ? [category] : []}
            onOptionSelect={(v) => setCategory(v[0] ?? "")}
          />
        </div>
        <div className="w-40 max-w-full">
          <SkyDropdown
            label="Code system"
            options={toOptions(opts.code_system)}
            placeholder="All"
            clearable
            selectedOptions={codeSystem ? [codeSystem] : []}
            onOptionSelect={(v) => setCodeSystem(v[0] ?? "")}
          />
        </div>
        <div className="w-40 max-w-full">
          <SkyDropdown
            label="Verification"
            options={toOptions(opts.verification_status)}
            placeholder="All"
            clearable
            selectedOptions={verification ? [verification] : []}
            onOptionSelect={(v) => setVerification(v[0] ?? "")}
          />
        </div>
        <div className="w-40 max-w-full">
          <SkyDropdown
            label="Sort by recorded"
            options={[
              { value: "desc", text: "Newest first" },
              { value: "asc", text: "Oldest first" },
            ]}
            selectedOptions={[sortDir]}
            onOptionSelect={(v) => setSortDir(v[0] === "asc" ? "asc" : "desc")}
          />
        </div>
      </div>
      <SkyText size={200}>
        {filtered.length} of {rows.length} problems
      </SkyText>
      <SkyDataGrid<Condition>
        data={filtered}
        columns={conditionColumns}
        getRowId={(r) => r.condition_key}
        emptyMessage="No problems match the current filters"
      />
    </div>
  );
}

/** Identity is a small key/value block (minimum-necessary; never MRN/medicare/medicaid). It is STICKY, so
 *  P0-2b surfaces the compact AllergyChip here: the allergy signal then stays visible at every scroll
 *  position (the point-of-order safety requirement) without a second stacked sticky bar. */
function IdentityHeader({ section, allergy }: { section: Section<unknown>; allergy: AllergyState }) {
  const id = section.data as MemberHeader;
  // P1-3 geriatric anchor: pair the raw DOB with a GUARDED derived age ("1946-03-02 (Age 78)"); an
  // implausible/future/missing sandbox DOB reads "Age unknown", never a wrong number.
  const dobLabel = id.date_of_birth
    ? `${id.date_of_birth} (${ageLabel(id.date_of_birth)})`
    : "Not recorded";
  const fields: Array<[string, string]> = [
    ["Date of birth", dobLabel],
    ["Gender", id.gender ?? "Not recorded"],
    ["Enrollment status", id.enrollment_status ?? "Not recorded"],
    ["Chronic conditions", String(id.chronic_condition_count ?? 0)],
  ];
  return (
    <div className="sticky top-0 z-10">
      <SkyCard>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <SkyText size={500} weight="semibold">
              {id.last_name}, {id.first_name}
            </SkyText>
            <AllergyChip state={allergy} />
          </div>
          <div className="flex items-center gap-2">
            <SkyText size={200}>Provenance</SkyText>
            <ProvenanceBadge meta={section.provenance} />
          </div>
        </div>
        <dl className="mt-2 flex flex-wrap gap-x-8 gap-y-2">
          {fields.map(([k, v]) => (
            <div key={k} className="flex flex-col">
              <SkyText size={200}>{k}</SkyText>
              <SkyText size={300} weight="medium">
                {v}
              </SkyText>
            </div>
          ))}
        </dl>
      </SkyCard>
    </div>
  );
}

/** A "view full history" footer for a summary section: deep-links to the dedicated full screen
 *  preselected to THIS member. For capped sections it adds an honest "most recent N" line — never the
 *  capped count presented as the member's total. */
function FullScreenLink({ def, section, memberKey }: { def: TabDef; section: Section<unknown>; memberKey: string }) {
  const navigate = useNavigate();
  const full = FULL_SCREEN[def.value];
  // Only offer "view more" when there are rows to view; an empty/error section shows its own state.
  if (!full || section.status !== "ok" || !memberKey) return null;
  const label = def.label.toLowerCase();
  const recentN = full.capped ? asRows(section.data).length : null;
  const to = full.to(memberKey);
  return (
    <div className="mt-3 flex flex-col gap-1">
      {recentN != null && (
        <SkyText size={200}>
          Showing the most recent {recentN} {label} in this summary view, not the member&apos;s full total.
        </SkyText>
      )}
      <SkyLink
        href={to}
        onClick={(e) => {
          e.preventDefault();
          navigate(to);
        }}
      >
        {full.capped ? `View all ${label} for this member` : `View full ${full.screen} for this member`}
      </SkyLink>
    </div>
  );
}

/** A4 — honest REAL-empty for Diagnostic Reports. When THIS member genuinely has no reports on file it is a
 *  real empty (not a feed gap); we still offer a one-click jump to a data-rich exemplar member (reusing the
 *  exemplar pattern) so the tab is never a dead end on first load. The exemplar key is a non-PHI surrogate. */
function DiagnosticReportsEmpty() {
  const navigate = useNavigate();
  const { key: exemplar } = useExemplarMember("diagnostic_reports");
  return (
    <div className="flex flex-col gap-2">
      <SkyText size={300}>
        None on record. This member has no diagnostic reports on file (a real empty, not a feed gap).
      </SkyText>
      {exemplar && exemplar !== "" && (
        <SkyLink
          href={`/member/${exemplar}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/member/${exemplar}`);
          }}
        >
          View a member with diagnostic reports on file
        </SkyLink>
      )}
    </div>
  );
}

/** A4 — Diagnostic Reports / labs-on-file body. Owns its full state machine: a populated REAL grid with a
 *  fixed DEFERRED "Result value" lane (numeric values pending the HL7 ORU feed — never fabricated); a
 *  REAL-empty with an exemplar affordance; and feed-gated/error states delegated to the shared state node. */
function DiagnosticReportsBody({ section }: { section: Section<unknown> }) {
  if (section.status === "error") return <>{sectionStateNode(section)}</>;
  if (section.status === "empty") {
    // A feed-gated DEFERRED empty (gold table absent) keeps the shared "Awaiting feed" copy; a genuine
    // REAL-empty (member simply has no reports) gets the honest empty + exemplar affordance.
    const realEmpty = deriveBadge(section.provenance).tier === "REAL";
    return realEmpty ? <DiagnosticReportsEmpty /> : <>{sectionStateNode(section)}</>;
  }
  const rows = asRows<DiagnosticReport>(section.data);
  return (
    <div className="flex flex-col gap-2">
      <SkyText size={200} color="muted">
        Diagnostic reports / labs on file (name, date, status, linked encounter). Numeric RESULT VALUES are
        pending the numeric lab feed (HL7 ORU), so each row shows the report identity only. No value is shown
        or inferred, and none is fabricated.
      </SkyText>
      <SkyDataGrid<DiagnosticReport>
        data={rows}
        columns={diagnosticReportColumns}
        getRowId={(r) => r.id}
        emptyMessage="No diagnostic reports on file"
      />
    </div>
  );
}

/** Render the active tab's section content (with its own provenance badge in the card header). */
function ActiveSection({ def, data, memberKey }: { def: TabDef; data: Member360; memberKey: string }) {
  const sectionName = def.section!;
  const section = data[sectionName];
  if (!section) {
    return (
      <SkyCard header={{ title: def.label }}>
        <SkyText size={300}>This section is not part of the loaded record.</SkyText>
      </SkyCard>
    );
  }
  // A4: the Diagnostic Reports tab owns its own state machine (REAL grid with a DEFERRED value lane,
  // REAL-empty with an exemplar affordance), so it bypasses the generic section-state node below.
  if (def.value === "diagnostic-reports") {
    return (
      <SkyCard header={{ title: def.label, action: <ProvenanceBadge meta={section.provenance} /> }}>
        <DiagnosticReportsBody section={section} />
      </SkyCard>
    );
  }
  const state = sectionStateNode(section);
  let body: ReactNode;
  if (state) {
    body = state;
  } else {
    switch (def.value) {
      case "allergies": {
        // Safety-critical: a drifted non-array payload must NOT read as "No known allergies recorded".
        const drift = safetyDriftNode(section.data, "Allergies");
        body = drift ?? (
          <SkyDataGrid<Allergy>
            data={asRows<Allergy>(section.data)}
            columns={allergyColumns}
            getRowId={(r) => r.id}
            emptyMessage="No known allergies recorded"
          />
        );
        break;
      }
      case "problem-list":
        body = <ProblemListBody rows={asRows<Condition>(section.data)} />;
        break;
      case "vitals":
        body = (
          <SkyDataGrid<Vital>
            data={asRows<Vital>(section.data)}
            columns={vitalColumns}
            getRowId={(r) => r.code_key}
            emptyMessage="Awaiting feed"
          />
        );
        break;
      case "encounters":
        body = (
          <SkyDataGrid<Encounter>
            data={asRows<Encounter>(section.data)}
            columns={encounterColumns}
            getRowId={(_r, i) => String(i)}
            emptyMessage="Awaiting feed"
          />
        );
        break;
      case "immunizations":
        body = (
          <SkyDataGrid<Immunization>
            data={asRows<Immunization>(section.data)}
            columns={immunizationColumns}
            getRowId={(r) => r.id}
            emptyMessage="No immunizations recorded"
          />
        );
        break;
      case "procedures":
        body = (
          <SkyDataGrid<Procedure>
            data={asRows<Procedure>(section.data)}
            columns={procedureColumns}
            getRowId={(r) => r.id}
            emptyMessage="No procedures recorded"
          />
        );
        break;
      case "coverage":
        body = (
          <SkyDataGrid<Coverage>
            data={asRows<Coverage>(section.data)}
            columns={coverageColumns}
            getRowId={(r) => r.id}
            emptyMessage="No coverage on record"
          />
        );
        break;
      case "care-plan":
        body = (
          <SkyDataGrid<import("@shared/contracts").CarePlan>
            // Guard the bundle: a non-object/non-array carePlans field degrades to an empty grid.
            data={asRows<import("@shared/contracts").CarePlan>((section.data as CarePlanBundle)?.carePlans)}
            columns={carePlanColumns}
            getRowId={(_r, i) => String(i)}
            emptyMessage="No coded care plans on record"
          />
        );
        break;
      case "goals":
        body = (
          <SkyDataGrid<import("@shared/contracts").Goal>
            data={asRows<import("@shared/contracts").Goal>((section.data as CarePlanBundle)?.goals)}
            columns={goalColumns}
            getRowId={(_r, i) => String(i)}
            emptyMessage="No goals recorded for this member"
          />
        );
        break;
      case "care-gaps":
        body = (
          <SkyDataGrid<CareGap>
            data={asRows<CareGap>(section.data)}
            columns={careGapColumns}
            getRowId={(r) => r.care_gap_key}
            emptyMessage="Awaiting feed"
          />
        );
        break;
      case "medications": {
        // Safety-critical: a drifted non-array payload must NOT read as "No medications on record".
        const drift = safetyDriftNode(section.data, "Medications");
        body = drift ?? (
          <div className="flex flex-col gap-2">
            {/* Drug identity is null in the current silver extract: this is medication ORDER activity,
                not a reconcilable med list. No polypharmacy / Beers / interaction determination is made. */}
            <SkyText size={200} color="muted">
              Medication order activity (all statuses). Drug identity is pending the coded-RxNorm feed, so
              this is not a reconcilable medication list and supports no polypharmacy or interaction screen.
            </SkyText>
            <SkyDataGrid<Medication>
              data={asRows<Medication>(section.data)}
              columns={medicationColumns}
              getRowId={(r) => r.id}
              emptyMessage="No medications on record"
            />
          </div>
        );
        break;
      }
      default:
        body = <SkyText size={300}>No renderer for this section.</SkyText>;
    }
  }
  return (
    <SkyCard header={{ title: def.label, action: <ProvenanceBadge meta={section.provenance} /> }}>
      {body}
      <FullScreenLink def={def} section={section} memberKey={memberKey} />
    </SkyCard>
  );
}

/** P1-4 (safety) — does an allergy warrant the highest-urgency (danger) treatment? FHIR criticality is
 *  frequently null even for a clinically severe reaction, so we do NOT under-signal: an ACTIVE allergy
 *  escalates to danger when criticality is "high", OR reaction_severity is "severe", OR criticality is
 *  ambiguous/null/unable-to-assess (default an unproven active allergy UP, never down). A clearly-resolved
 *  ("inactive"/"resolved") allergy does not escalate; an explicit "low" criticality with a non-severe
 *  reaction stays at warning. clinical_status null is treated as active (presume active when unknown). */
function isHighUrgencyAllergy(row: Allergy): boolean {
  const status = (row.clinical_status ?? "").toLowerCase();
  const isActive = status === "" || status === "active";
  if (!isActive) return false;
  const crit = (row.criticality ?? "").toLowerCase();
  const sev = (row.reaction_severity ?? "").toLowerCase();
  if (crit === "high") return true;
  if (sev === "severe") return true;
  // Ambiguous / unknown criticality on an active allergy -> treat as high urgency (do not under-signal).
  return crit === "" || crit === "unable-to-assess" || crit === "unknown";
}

/** The single derived allergy signal, shared by the sticky IdentityHeader chip (P0-2b, always visible) and
 *  the detailed AllergyBanner (P0-2a). Computed once in Member360 so the chip and the banner can never
 *  disagree. "unknown" = the allergy feed has not landed (DEFERRED) OR the section errored -> it must NEVER
 *  read as "no allergies"; "real-empty" = a genuine confirmed-absent "No known allergies". */
type AllergyState =
  | { kind: "has"; count: number; highCount: number; names: string[] }
  | { kind: "real-empty" }
  | { kind: "unknown" }
  | { kind: "absent" };

function deriveAllergyState(section: Section<unknown> | undefined): AllergyState {
  if (!section) return { kind: "absent" };
  // P0-2: an errored allergy section is clinically UNKNOWN, not absent -> flag, never reassure.
  if (section.status === "error") return { kind: "unknown" };
  if (section.status === "ok") {
    // Contract-drift guard: an 'ok' section whose payload is NOT an array is clinically UNKNOWN, never
    // "no allergies" - do not reassure (and never .filter() a non-array) on a shape we cannot read.
    if (!Array.isArray(section.data)) return { kind: "unknown" };
    const rows = section.data as Allergy[];
    const highCount = rows.filter(isHighUrgencyAllergy).length;
    const names = rows.map((r) => r.substance_display).filter((s): s is string => Boolean(s));
    return { kind: "has", count: rows.length, highCount, names };
  }
  // Empty: a REAL-empty is "No known allergies"; a feed-gated DEFERRED empty is UNKNOWN (not confirmed absent).
  const realEmpty = deriveBadge(section.provenance).tier === "REAL";
  return realEmpty ? { kind: "real-empty" } : { kind: "unknown" };
}

/** P0-2b — the compact, ALWAYS-VISIBLE allergy chip rendered inside the sticky IdentityHeader, so the
 *  allergy signal never scrolls out of view at the point of order. Color + label (never color-only):
 *  high-urgency -> danger, other active allergies -> warning, UNKNOWN -> warning, real-empty -> success. */
function AllergyChip({ state }: { state: AllergyState }) {
  if (state.kind === "absent") return null;
  let color: SkyBadgeColor;
  let label: string;
  if (state.kind === "has") {
    color = state.highCount > 0 ? "danger" : "warning";
    label =
      state.highCount > 0
        ? `Allergies: ${state.count} (${state.highCount} high)`
        : `Allergies: ${state.count}`;
  } else if (state.kind === "real-empty") {
    color = "success";
    label = "Allergies: none known";
  } else {
    color = "warning";
    label = "Allergies: UNKNOWN";
  }
  return (
    <SkyBadge color={color} appearance="tint" size="small">
      {label}
    </SkyBadge>
  );
}

/** Persistent, safety-first allergy banner pinned at the top of the record. Badges honestly from the shared
 *  AllergyState: substantive allergies -> a danger/warning message-bar listing substances (high-urgency
 *  escalates to danger via isHighUrgencyAllergy); a REAL-empty member -> a success "No known allergies
 *  recorded"; a feed-gated/errored UNKNOWN -> a WARNING "Allergies UNKNOWN" (never the old calm info-blue,
 *  which misreads as "no allergies" before ordering). Never a silent gap, never a faked "no allergies". */
function AllergyBanner({ state }: { state: AllergyState }) {
  if (state.kind === "absent") return null;
  if (state.kind === "has") {
    const shown = state.names.slice(0, 6).join(", ");
    const more = state.names.length > 6 ? ` (+${state.names.length - 6} more)` : "";
    const detail = shown ? `${shown}${more}` : `${state.count} recorded allergy intolerance(s)`;
    return (
      <SkyMessageBar
        intent={state.highCount > 0 ? "error" : "warning"}
        title={`Allergies on record (${state.count})`}
        message={`${detail}. Review the Allergies tab before prescribing or ordering.`}
      />
    );
  }
  if (state.kind === "real-empty") {
    return (
      <SkyMessageBar
        intent="success"
        title="No known allergies recorded"
        message="No allergy intolerance is recorded for this member (a real empty, not a feed gap)."
      />
    );
  }
  // UNKNOWN: feed not landed OR section errored. Warning + explicit wording; NOT a confirmed absence.
  return (
    <SkyMessageBar
      intent="warning"
      title="Allergies UNKNOWN - not confirmed absent"
      message="The allergy feed has not landed for this member, so allergies are unknown. Do not treat this as no known allergies before prescribing or ordering."
    />
  );
}

export function Member360() {
  const { key } = useParams();
  const [searchParams] = useSearchParams();
  const { data, loading, error, retry } = usePage<Member360>(
    key ? `/api/v1/participants/${key}/360` : null,
    member360Schema,
  );
  // Deep-linkable active tab (?tab=…): a row/affordance can land directly on a section (e.g.
  // ?tab=diagnostic-reports). Falls back to Problem List for an unknown/deferred/missing tab.
  const requestedTab = searchParams.get("tab");
  const [active, setActive] = useState(
    requestedTab && TAB_DEFS.some((t) => t.value === requestedTab && !t.deferred)
      ? requestedTab
      : "problem-list",
  );

  // M5: the two always-disabled DEFERRED tabs (Quality, HCC) are moved OUT of the primary strip into a
  // trailing caption cluster below. P2: the remaining primary tabs are grouped into wrapping CLUSTERS
  // (Clinical / Coverage / Plan) so they fit without a horizontal-scroll row that clipped the last tab.
  // Allergies still leads (safety). The deferred sections are never hidden — they show as a labelled
  // cluster with the feed that unblocks each.
  const primaryDefs = TAB_DEFS.filter((t) => !t.deferred);
  const deferredDefs = TAB_DEFS.filter((t) => t.deferred);
  // visible-but-disabled when the section is absent from the loaded composite, never hidden.
  const toTab = (t: TabDef): TabItem => ({
    value: t.value,
    label: t.label,
    disabled: t.section ? !(data && t.section in data) : true,
  });
  // Build the clustered tab groups. A primary tab not named in any cluster falls into a trailing "More"
  // cluster so it is ALWAYS rendered/reachable (never silently dropped). Clusters with no tabs are omitted.
  const clusteredValues = new Set(TAB_CLUSTERS.flatMap((c) => c.values));
  const tabClusters = [
    ...TAB_CLUSTERS.map((c) => ({
      label: c.label,
      values: c.values,
      tabs: c.values
        .map((v) => primaryDefs.find((t) => t.value === v))
        .filter((t): t is TabDef => Boolean(t))
        .map(toTab),
    })),
    {
      label: "More",
      values: primaryDefs.filter((t) => !clusteredValues.has(t.value)).map((t) => t.value),
      tabs: primaryDefs.filter((t) => !clusteredValues.has(t.value)).map(toTab),
    },
  ].filter((c) => c.tabs.length > 0);

  const activeDef = TAB_DEFS.find((t) => t.value === active) ?? TAB_DEFS[0];

  return (
    <>
      <PageHeader
        crumbs={[
          { key: "home", label: "skyCare" },
          { key: "roster", label: "Participants", to: "/roster" },
          { key: "member", label: `Member ${key ?? ""}`.trim() },
        ]}
        title="Member 360"
        description="Composite participant record read live from the skyCare Sandbox operation database. Each section shows where its data came from; an empty section is awaiting data, not a zero. Quality and HCC load on their own surfaces below the tabs."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        {loading && <SkyLoader label="Loading member record" />}
        {error && <PageError title="Could not load member record" message={error} onRetry={retry} />}
        {data && data.identity && (
          <IdentityHeader section={data.identity} allergy={deriveAllergyState(data.allergies)} />
        )}
        {data && <AllergyBanner state={deriveAllergyState(data.allergies)} />}
        {data && (
          <>
            {/* Primary tabs grouped into wrapping clusters (Allergies leads Clinical for safety). The
                groups WRAP onto the next line rather than scrolling, so no tab is ever clipped and every
                tab stays reachable on any viewport. Each cluster's SkyTabList highlights the active tab
                only when it belongs to that cluster. The two deferred sections render as a labelled
                cluster below. */}
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {tabClusters.map((cluster) => (
                <div key={cluster.label} className="flex flex-col gap-1">
                  <SkyText size={100} color="muted" weight="medium">
                    {cluster.label}
                  </SkyText>
                  <SkyTabList
                    tabs={cluster.tabs}
                    selectedValue={cluster.values.includes(active) ? active : ""}
                    onTabSelect={setActive}
                  />
                </div>
              ))}
            </div>
            {deferredDefs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <SkyText size={200} color="muted">
                  Deferred sections (not part of this composite):
                </SkyText>
                {deferredDefs.map((t) => (
                  <SkyBadge key={t.value} color="subtle" appearance="tint" size="small">
                    {t.label} (awaits {t.deferredFeed})
                  </SkyBadge>
                ))}
              </div>
            )}
            {activeDef.deferred ? (
              <SkyCard header={{ title: activeDef.label }}>
                <SkyText size={300}>
                  This section is not loaded in the Member 360 composite. It awaits its{" "}
                  {activeDef.deferredFeed}.
                </SkyText>
              </SkyCard>
            ) : (
              <ActiveSection def={activeDef} data={data} memberKey={key ?? ""} />
            )}
          </>
        )}
      </section>
      <PageFooter />
    </>
  );
}

/** Keyless /member entry — resolve the conditions-richest REAL member and redirect into their record so
 *  the composite never opens on an empty default member. If no member resolves (table absent/empty),
 *  redirect to the roster so the user can pick one (never a fabricated key). */
export function MemberDefault() {
  const { key, loading } = useExemplarMember("conditions");
  if (key) return <Navigate to={`/member/${key}`} replace />;
  if (!loading) return <Navigate to="/roster" replace />;
  return <SkyLoader label="Loading member record" />;
}
